import type React from "react";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../hooks/use-media-query";
import type { Omittable } from "../../lib/props";

export type PanelOverlaySide = "right" | "left";
export type PanelOverlaySize = "sm" | "md" | "lg";

const SIZE_CLASSES: Record<PanelOverlaySize, string> = {
  sm: "w-80", // 320px
  md: "w-[28rem]", // 448px
  lg: "w-[35rem]", // 560px
};

// Backdrop fade — kept in sync with the panel's 200ms slide.
const BACKDROP_ANIMATION = "animate-in fade-in-0 duration-200";

export type PanelOverlayProps = Omittable<{
  /** Which edge to anchor the panel to. Default: 'right' */
  side?: PanelOverlaySide;
  /** Panel width preset. Default: 'md' (448px) */
  size?: PanelOverlaySize;
  /**
   * When provided, renders a dimmed backdrop behind the panel. Clicking the
   * backdrop calls this callback (typically to close the panel).
   */
  onDismiss?: () => void;
  children?: React.ReactNode;
}>;

/**
 * An absolutely-positioned panel that overlays its positioned container,
 * anchored to one edge and spanning full height. On mobile it fills the
 * container instead of sliding in from one side.
 *
 * The parent must be `position="relative"` — Box is `position: static` by
 * default, so the panel would otherwise escape to the nearest positioned
 * ancestor.
 *
 * This is NOT a dialog (no portal, no focus trap, no backdrop layer of its own)
 * — it overlays one container, which is why `Sheet`/`Drawer` cannot express it.
 * Domain-free, hence the particle tier.
 */
export function PanelOverlay({
  side = "right",
  size = "md",
  onDismiss,
  children,
}: PanelOverlayProps) {
  const isMobile = useIsMobile();

  // On mobile: fill container. On desktop: fixed width panel with border.
  const panelClasses = cn(
    "bg-card flex flex-col overflow-hidden",
    isMobile
      ? "flex-1"
      : cn(
          "border-border shadow-lg",
          side === "right" ? "border-l" : "border-r",
          SIZE_CLASSES[size],
        ),
  );

  // Slide in from the anchored edge on mount. Mobile is full-bleed with no
  // edge to slide from, so fade instead.
  const panelAnimation = isMobile
    ? BACKDROP_ANIMATION
    : cn(
        "animate-in duration-200",
        side === "right" ? "slide-in-from-right" : "slide-in-from-left",
      );

  if (onDismiss) {
    return (
      <Box
        // z-50 establishes a stacking context above overlaid content
        // (e.g. DataTable sticky headers z-20/z-30 and bulk-actions bar z-50).
        position="absolute"
        inset={0}
        zIndex={50}
        // Mobile is full-bleed with no scrim, so it stays a plain block. On
        // desktop the scrim and the panel are a row: the panel sits on the
        // anchored edge and the scrim takes the rest, so the row REVERSES for
        // a left-anchored panel.
        direction={
          isMobile ? undefined : side === "right" ? "row" : "row-reverse"
        }
      >
        {!isMobile && (
          <Box flex={1}
            as="button"
            aria-label="Close panel"
            onClick={onDismiss}
            className={cn("bg-black/30", BACKDROP_ANIMATION)}
          />
        )}
        <Box fill="height" className={cn(panelClasses, panelAnimation)}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      position="absolute"
      zIndex={50}
      className={cn(
        // Mobile fills; desktop anchors to the side with a fixed width
        // (panelClasses). `inset-0` would pin left+right and override the
        // width, so only fill on mobile.
        isMobile
          ? "inset-0"
          : cn("inset-y-0", side === "right" ? "right-0" : "left-0"),
        panelClasses,
        panelAnimation,
      )}
    >
      {children}
    </Box>
  );
}
